import { CONST } from '@common/constants/const.constant';
import { EMPTY_LOQATE_ADDRESS, IInputLoqateValue } from '@common/general-ui/input-loqate/input-loqate.component';
import { authService } from '@common/services/auth.service';
import { consoleService } from '@common/services/console.service';
import { cookieUtils } from '@common/utils/cookie.util';
import { createCollection } from '@common/utils/create-collection.util';
import { apiHooks } from '@src/api/hooks/api-hooks';
import { createDeliveryConfigDto } from '@src/api/model/delivery-config.dto';
import { LINKS } from '@src/shared/links.constant';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router';
import { RegisterRoutes } from './register-routes.component';
import { useRegister } from './register.hook';

export const RegisterContainer = (): JSX.Element => {
    const { registerInfo, saveToRegister } = useRegister();

    const history = useHistory();

    const query = useMemo(() => new URLSearchParams(history.location.search), [history]);
    const [queryPostcode, setQueryPostcode] = useState<string>(query.get(CONST.queryPostcode) || '');
    const [sourceSku, setSourceSKU] = useState<string>(
        query.get(CONST.querySKU) || cookieUtils.getCookie(CONST.cookieSku) || '',
    );
    const [couponStr, setCouponStr] = useState<string>('');

    const {
        mutateAsync,
        error: validatePostcodeError,
        isSuccess: validatePostcodeSuccess,
        isLoading: validatePostcodeLoading,
        reset: validatePostcodeReset,
    } = apiHooks.packHouse.useValidatePostCode();

    const { data: products } = apiHooks.packHouse.useGetProducts({ enabled: !!sourceSku });

    const { data: couponData, mutate: couponMutate, isLoading: isCouponLoading } = apiHooks.item.useGetCouponById();

    // if user is logged in, redirect to account page
    if (history.location.pathname !== LINKS.register.summary.toString()) {
        if (authService.getInfo()?.id_token) {
            history.push(LINKS.account.manageBox.toString());
        }
    }

    // Handle coupon in a cookie or query string and save to registration session storage
    useEffect(() => {
        if (!couponStr) {
            const couponCode = query.get(CONST.queryCoupon) || cookieUtils.getCookie(CONST.cookieCoupon) || '';

            if (couponCode && registerInfo.couponId !== couponCode) {
                // cleanup coupon cookie
                cookieUtils.eraseCookie(CONST.cookieCoupon, '');
                couponMutate(couponCode);

                // store coupon string
                saveToRegister({ couponId: couponCode });
                setCouponStr(couponCode);
            }
        }
    }, [registerInfo, couponStr, query, saveToRegister, couponMutate, couponData]);

    const savePostcodeToRegister = useCallback(
        (postcode: string): void => {
            const storedShippingAddress = registerInfo.shippingAddress;
            if (decodeURI(postcode) !== storedShippingAddress.postCode) {
                const shippingAddress: IInputLoqateValue = {
                    city: storedShippingAddress.city,
                    addressLine1: storedShippingAddress.addressLine1,
                    addressLine2: storedShippingAddress.addressLine2,
                    postCode: decodeURI(postcode),
                    addressLine3: storedShippingAddress.addressLine3,
                    country: storedShippingAddress.country,
                    label: storedShippingAddress.label || '',
                    originalLabel: storedShippingAddress.originalLabel || '',
                };

                saveToRegister({ isCheckPostCodeStepValid: true, shippingAddress });
                const url = LINKS.register.firstBox.toString();
                history.push(url);
            }
        },
        [registerInfo, history, saveToRegister],
    );

    useEffect(() => {
        if (queryPostcode) {
            mutateAsync({ post_code: queryPostcode }).then((res) => {
                if (res.valid) {
                    const deliveryConfigs = createCollection(
                        (res.list || []).map((e) => createDeliveryConfigDto(e)) || [],
                        0,
                        {},
                    );
                    const activeDeliveryConfigs = deliveryConfigs?.list?.filter((f) => f.active && !f.hidden);
                    const updatedDeliveryConfig = activeDeliveryConfigs?.length ? activeDeliveryConfigs[0] : undefined;

                    if (updatedDeliveryConfig) {
                        saveToRegister({ deliveryConfigId: updatedDeliveryConfig.id });
                    }
                }
            });
        }
    }, [queryPostcode, mutateAsync, saveToRegister]);

    useEffect(() => {
        if (!queryPostcode) {
            return;
        }

        if (validatePostcodeSuccess) {
            savePostcodeToRegister(queryPostcode);
            validatePostcodeReset();
        } else if (validatePostcodeError) {
            saveToRegister({ shippingAddress: EMPTY_LOQATE_ADDRESS, isCheckPostCodeStepValid: false });
            setQueryPostcode('');
        }
    }, [
        validatePostcodeSuccess,
        validatePostcodeError,
        saveToRegister,
        savePostcodeToRegister,
        queryPostcode,
        validatePostcodeReset,
    ]);

    useEffect(() => {
        if (!sourceSku) {
            setSourceSKU('');
            return;
        }

        if (sourceSku && products?.list && products?.list.length > 0) {
            cookieUtils.eraseCookie(CONST.querySKU, '');
            const selectedProduct = products.list.find((product) => product.id === sourceSku.toUpperCase());

            if (selectedProduct) {
                const price = selectedProduct.item?.prices.find(
                    (price) => price.period === 1 && price.period_unit === 'week',
                );
                saveToRegister({
                    productId: selectedProduct.id,
                    quantity: price?.period,
                    priceId: price?.id,
                });
            }
        }
    }, [sourceSku, products, saveToRegister, registerInfo]);

    // handle landing page cookie
    const landingCookie = cookieUtils.getCookie(CONST.cookieLandingUrl) || '';
    if (landingCookie) {
        try {
            const landingUrl = new URL(landingCookie);
            const params = new URLSearchParams(landingUrl.search);
            const validParams = ['fbclid', 'irclickid'];

            params.forEach((_, key) => {
                if (!validParams.includes(key)) {
                    landingUrl.searchParams.delete(key);
                }
            });
            if (landingUrl.toString().length <= 250) {
                saveToRegister({ ...registerInfo, landingUrl: landingUrl.toString() });
            } else {
                consoleService.log('unexpected landing url length: ', landingUrl);
            }
        } catch (error) {
            consoleService.log('error parsing landing cookie: ', error);
        }
    }

    return <RegisterRoutes isLoading={isCouponLoading || validatePostcodeLoading} />;
};
